import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { Icon } from 'tea-component';
import { IStudio, COUNTRY_COORDINATES, COUNTRY_FULL_NAME } from '../../const/studio';
import { STUDIO_ICON_MAP } from '../../const/iconMap';
import { importJSON } from '@intelligence/utils/echartsUtils';
import { generateStudioBadges } from '../../utils/badgeGenerator';
import Loading from '@/pages/dashboard/components/Loading';
import './index.scss';

interface IStudioMapProps {
  studios: IStudio[];
  onIconHover?: (studioName: string | string[]) => void;
  onIconLeave?: () => void;
  onChartReady?: (instance: EChartsType) => void;
  chartRef: React.RefObject<HTMLDivElement>;
  hoveredChartRef?: React.RefObject<HTMLDivElement>;
  hoveredStudioName?: string | string[] | null;
}

const mapCache: {
  worldMap: any;
  badges: Record<string, string>;
} = {
  worldMap: null,
  badges: {},
};

const StudioMap: React.FC<IStudioMapProps> = ({ studios, onIconHover, onIconLeave, onChartReady, chartRef, hoveredChartRef, hoveredStudioName }) => {

  const chartInstance = useRef<EChartsType>();
  const hoveredChartInstance = useRef<EChartsType>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [badgesLoading, setBadgesLoading] = useState(true);
  const isMounted = useRef(true);
  const badgeMapRef = useRef<Record<string, string>>({});
  const studioSeriesIndexMap = useRef<Record<string, number>>({});
  const studioBaseSymbolSizeMap = useRef<Record<string, number>>({});
  const studioCountryNameMap = useRef<Record<string, string>>({});

  // 地图缩放状态
  const [mapZoom, setMapZoom] = useState<{
    type: 'zoomIn' | 'zoomOut';
    toChange: number;
  }>({ type: 'zoomOut', toChange: 0 });

  const DEFAULT_ZOOM = 1.2;
  const MIN_ZOOM = 1.2;
  const MAX_ZOOM = 10;

  // 欧洲国家列表
  const EURO_COUNTRIES = new Set([
    'UK',
    'Sweden',
    'Norway',
    'Poland',
    'Germany',
  ]);

  // 构建 studio 和 sub_studio 的双向关系映射
  const studioRelationMap = useMemo(() => {
    const relationMap: Record<string, string> = {};
    studios.forEach((studio) => {
      if (studio.sub_studio) {
        // 主 studio -> sub_studio
        relationMap[studio.name] = studio.sub_studio.name;
        // sub_studio -> 主 studio
        relationMap[studio.sub_studio.name] = studio.name;
      }
    });
    return relationMap;
  }, [studios]);

  // 按国家统计工作室数据（包含 sub_studio）
  const studiosByCountry = useMemo(() => {
    const groupedStudios: { [country: string]: { name: string; isSubStudio: boolean; parentStudio?: string; }[]; } = {};
    studios.forEach((studio) => {
      if (!groupedStudios[studio.country]) {
        groupedStudios[studio.country] = [];
      }
      groupedStudios[studio.country].push({ name: studio.name, isSubStudio: false });

      // 如果有 sub_studio，也添加到对应国家
      if (studio.sub_studio) {
        const subCountry = studio.sub_studio.country;
        if (!groupedStudios[subCountry]) {
          groupedStudios[subCountry] = [];
        }
        groupedStudios[subCountry].push({
          name: studio.sub_studio.name,
          isSubStudio: true,
          parentStudio: studio.name,
        });
      }
    });
    return groupedStudios;
  }, [studios]);

  // 准备国家数据，包含国家坐标和所有工作室icon（包括 sub_studio）
  const countryMarkerData = useMemo(() => {
    return Object.entries(studiosByCountry)
      .map(([country, countryStudios]) => {
        const coordinates = COUNTRY_COORDINATES[country];
        if (!coordinates) return null;

        return {
          country,
          coordinates,
          studios: countryStudios,
        };
      })
      .filter(Boolean);
  }, [studiosByCountry]);

  // 加载地图数据和徽章
  useEffect(() => {
    const loadMapData = async () => {
      try {
        // 检查是否已经有缓存
        if (mapCache.worldMap) {
          if (isMounted.current) {
            setMapLoading(false);
          }
          return;
        }

        // 检查echarts是否已注册世界地图
        if (echarts.getMap('world')) {
          if (isMounted.current) {
            setMapLoading(false);
          }
        } else {
          const mapData = await importJSON(
            'https://ogdb-cdn.intlgame.cn/front/static/world.json',
          );
          if (mapData && isMounted.current) {
            mapCache.worldMap = mapData;
            echarts.registerMap('world', mapData as any);
            setMapLoading(false);
          }
        }
      } catch (e) {
        console.error('Failed to load map data:', e);
        if (isMounted.current) {
          setMapLoading(false);
        }
      }
    };

    const loadBadges = async () => {
      try {
        // 使用缓存的徽章
        if (Object.keys(mapCache.badges).length > 0) {
          badgeMapRef.current = mapCache.badges;
          if (isMounted.current) {
            setBadgesLoading(false);
          }
          return;
        }

        // 生成徽章
        const badges = await generateStudioBadges(
          studios,
          (name: string) =>
            STUDIO_ICON_MAP[name as keyof typeof STUDIO_ICON_MAP],
        );

        if (isMounted.current) {
          badgeMapRef.current = badges;
          mapCache.badges = badges;
          setBadgesLoading(false);
        }
      } catch (e) {
        console.error('Failed to load badges:', e);
        if (isMounted.current) {
          setBadgesLoading(false);
        }
      }
    };

    loadMapData();
    loadBadges();

    return () => {
      isMounted.current = false;
    };
  }, [studios]);

  // 准备国家数据用于地图着色
  const countryData = useMemo(() => {
    const countriesWithStudio = new Set(Object.keys(studiosByCountry));

    return Array.from(countriesWithStudio).map((country) => {
      const realCountry = COUNTRY_FULL_NAME?.[country] || country;
      return {
        name: realCountry,
        value: 1, // 1 表示有 studio
      };
    });
  }, [studiosByCountry]);

  // 初始化图表
  useEffect(() => {
    if (!chartRef.current || mapLoading || badgesLoading) return;

    chartInstance.current = echarts.getInstanceByDom(chartRef.current);
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, {
        renderer: 'canvas',
      });
    }

    // 通知父组件 chart 实例已就绪
    if (onChartReady && chartInstance.current) {
      onChartReady(chartInstance.current);
    }

    // 构建 series 数据：每个工作室一个 series
    const series: any[] = [];
    let seriesIndex = 1; // 从 1 开始，因为 series[0] 是 map

    // 清空旧的映射
    studioSeriesIndexMap.current = {};
    studioBaseSymbolSizeMap.current = {};
    studioCountryNameMap.current = {};

    countryMarkerData.forEach((marker: any) => {
      if (!marker) return;

      const { coordinates, studios } = marker;

      // 使用固定坐标点，coordinates 是一个坐标数组
      studios.forEach((studioInfo: { name: string; isSubStudio: boolean; parentStudio?: string; }, index: number) => {
        // 使用对应索引的坐标点，如果超出则使用第一个（中心点）
        const coord = coordinates[index] || coordinates[0];

        const badgeUrl = badgeMapRef.current[studioInfo.name];
        const isEuro = EURO_COUNTRIES.has(marker.country);
        const country = COUNTRY_FULL_NAME?.[marker.country] || marker.country;
        const iconSize = isEuro ? 16 : 26;

        // 记录 series 索引和基础大小
        studioSeriesIndexMap.current[studioInfo.name] = seriesIndex;
        studioBaseSymbolSizeMap.current[studioInfo.name] = iconSize;
        studioCountryNameMap.current[studioInfo.name] = country;

        series.push({
          id: studioInfo.name,
          type: 'scatter',
          coordinateSystem: 'geo',
          data: [
            {
              value: coord,
              studio: { name: studioInfo.name, isSubStudio: studioInfo.isSubStudio, parentStudio: studioInfo.parentStudio },
              country,
            },
          ],
          symbolSize: iconSize,
          symbol: `image://${badgeUrl || STUDIO_ICON_MAP[studioInfo.name as keyof typeof STUDIO_ICON_MAP]}`,
          itemStyle: {
            opacity: 1,
          },
          emphasis: {
            itemStyle: {
              symbolSize: iconSize + 10,
            },
          },
          tooltip: {
            show: false,
          },
        });

        seriesIndex += 1;
      });
    });

    series.unshift({
      type: 'map',
      map: 'world',
      geoIndex: 0,
      data: countryData,
      roam: false,
      select: {
        disabled: true
      }
    });

    const option = {
      tooltip: {
        show: false,
      },
      geo: {
        type: 'map',
        map: 'world',
        roam: 'move',
        zoom: 1.2,
        itemStyle: {
          areaColor: '#D8DAE4',
          borderWidth: 0
        },
        emphasis: {
          disabled: true,
        },
        select: {
          disabled: true,
          itemStyle: {
            areaColor: '#6A96F5',
          },
          label: {
            show: false
          }
        },
      },
      series,
      visualMap: {
        show: false,
        min: 0,
        max: 1,
        inRange: {
          color: ['#D8DAE4', '#6A96F5'],
        },
      },
    };

    chartInstance.current.setOption(option, true);

    // 添加 hover 事件监听
    chartInstance.current.on('mouseover', 'series', (params: any) => {
      if (params.componentSubType === 'scatter' && params.data?.studio) {
        const studioName = params.data.studio.name;
        // 检查是否有关联的 sub_studio
        const relatedStudio = studioRelationMap[studioName];
        if (relatedStudio) {
          // 有关联的 studio，返回两个 studio 名称
          onIconHover?.([studioName, relatedStudio]);
        } else {
          onIconHover?.(studioName);
        }
      }
    });

    chartInstance.current.on('mouseout', 'series', () => {
      onIconLeave?.();
    });

    // 监听地图拖拽事件，同步 hovered chart
    chartInstance.current.on('georoam', (params: any) => {
      if (hoveredChartInstance.current && params.type === 'georoam') {
        // 拖拽时清空 hovered chart
        hoveredChartInstance.current.setOption({
          series: [
            {
              id: 'hovered-map',
              data: [],
            },
          ],
        }, { replaceMerge: ['series'] });

        // 触发离开事件
        onIconLeave?.();

        const option = chartInstance.current?.getOption() as any;
        const geoCenter = option?.geo?.[0]?.center;
        const geoZoom = option?.geo?.[0]?.zoom;
        hoveredChartInstance.current.setOption({
          geo: {
            center: geoCenter,
            zoom: geoZoom,
          },
        });
      }
    });

    // 添加地图点击事件监听
    chartInstance.current.on('click', (params: any) => {
      if (params.componentSubType === 'map' && params.name) {
        const countryName = params.name;
        // 获取点击国家的所有 studio
        const selectedStudios: string[] = [];

        // 反向查找：从国家名找到对应的工作室
        Object.entries(studioCountryNameMap.current).forEach(([studioName, country]) => {
          if (country === countryName) {
            selectedStudios.push(studioName);
          }
        });

        // 扩展：如果选中的 studio 有关联的 sub_studio，也添加进来
        const expandedStudios = new Set(selectedStudios);
        selectedStudios.forEach((studioName) => {
          const relatedStudio = studioRelationMap[studioName];
          if (relatedStudio) {
            expandedStudios.add(relatedStudio);
          }
        });

        const finalStudios = Array.from(expandedStudios);
        if (finalStudios.length > 0) {
          // 检查是否点击的是同一个国家（通过比较 selectedStudios）
          // 这会由父组件的逻辑处理，这里直接调用
          onIconHover?.(finalStudios);
        }
      }
    });

    // 监听窗口 resize
    const handleResize = () => {
      chartInstance.current?.resize({
        animation: { duration: 300 },
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.off('mouseover');
      chartInstance.current?.off('mouseout');
      chartInstance.current?.off('click');
      chartInstance.current?.off('georoam');
      chartInstance.current?.dispose();
    };
  }, [countryMarkerData, mapLoading, badgesLoading, countryData, onIconHover, onIconLeave, onChartReady, chartRef]);

  // 初始化 hovered chart（用于在 mask 上层显示选中的国家和 studio icon）
  useEffect(() => {
    if (!hoveredChartRef?.current || mapLoading || badgesLoading) return;

    hoveredChartInstance.current = echarts.getInstanceByDom(hoveredChartRef.current);
    if (!hoveredChartInstance.current) {
      hoveredChartInstance.current = echarts.init(hoveredChartRef.current, undefined, {
        renderer: 'canvas',
      });
    }

    // 初始化空地图
    const option = {
      tooltip: { show: false },
      geo: {
        type: 'map',
        map: 'world',
        roam: false,
        zoom: 1.2,
        itemStyle: {
          areaColor: 'transparent',
          borderWidth: 0,
        },
        emphasis: { disabled: true },
        select: { disabled: true },
        silent: true,
      },
      series: [
        {
          id: 'hovered-map',
          type: 'map',
          map: 'world',
          geoIndex: 0,
          data: [],
          roam: false,
          itemStyle: {
            areaColor: 'transparent',
            borderWidth: 0,
          },
          silent: true,
        },
      ],
      visualMap: {
        show: false,
        min: 0,
        max: 1,
        inRange: {
          color: ['transparent', '#326FF0'],
        },
      },
    };

    hoveredChartInstance.current.setOption(option, true);

    const handleResize = () => {
      hoveredChartInstance.current?.resize({
        animation: { duration: 300 },
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      hoveredChartInstance.current?.dispose();
    };
  }, [hoveredChartRef, mapLoading, badgesLoading]);

  // 地图放大缩小
  useEffect(() => {
    if (!mapZoom || mapZoom.toChange === 0) return;
    if (!chartInstance.current) return;

    const option = chartInstance.current.getOption() as any;
    let currentZoom = option?.geo?.[0]?.zoom || DEFAULT_ZOOM;
    const currentCenter = option?.geo?.[0]?.center;

    if (mapZoom.type === 'zoomIn' && currentZoom < MAX_ZOOM) {
      currentZoom = Math.min(currentZoom + 0.4, MAX_ZOOM);
    } else if (mapZoom.type === 'zoomOut' && currentZoom > MIN_ZOOM) {
      currentZoom = Math.max(currentZoom - 0.4, MIN_ZOOM);
    }

    chartInstance.current.setOption({
      geo: { zoom: currentZoom },
    });

    // 同步 hovered chart 的 zoom 和 center
    if (hoveredChartInstance.current) {
      hoveredChartInstance.current.setOption({
        geo: {
          zoom: currentZoom,
          center: currentCenter,
        },
      });
    }
  }, [mapZoom.toChange]);

  // 更新 hovered chart 显示选中的国家和 studio icon
  useEffect(() => {
    if (!hoveredChartInstance.current || !hoveredChartRef?.current) return;

    if (!hoveredStudioName) {
      // 清空
      hoveredChartInstance.current.setOption({
        series: [
          {
            id: 'hovered-map',
            data: [],
          },
        ],
      }, { replaceMerge: ['series'] });
      return;
    }

    // 支持 string 和 string[] 两种类型
    const studioNames = Array.isArray(hoveredStudioName) ? hoveredStudioName : [hoveredStudioName];
    const selectedStudiosSet = new Set(studioNames);

    // 收集所有需要高亮的国家
    const countryNamesToHighlight: string[] = [];
    studioNames.forEach((name) => {
      const countryName = studioCountryNameMap.current[name];
      if (countryName && !countryNamesToHighlight.includes(countryName)) {
        countryNamesToHighlight.push(countryName);
      }
    });

    // 收集所有涉及的国家的 marker 数据
    const relevantMarkers = countryMarkerData.filter((marker: any) =>
      marker?.studios?.some((s: { name: string; }) => studioNames.includes(s.name))
    );

    if (relevantMarkers.length === 0) return;

    // 为每个涉及的国家渲染 studio icons
    const iconSeries: any[] = [];
    let iconIndex = 0;

    relevantMarkers.forEach((marker: any) => {
      const { coordinates, studios, country } = marker;
      const isEuro = EURO_COUNTRIES.has(country);

      studios.forEach((studioInfo: { name: string; }, index: number) => {
        // 使用对应索引的坐标点，如果超出则使用第一个（中心点）
        const coord = coordinates[index] || coordinates[0];
        const badgeUrl = badgeMapRef.current[studioInfo.name];
        const isSelected = selectedStudiosSet.has(studioInfo.name);
        let iconSize = isEuro ? 16 : 26;
        if (isSelected) {
          iconSize = isEuro ? 26 : 36;
        }

        iconSeries.push({
          id: `hovered-icon-${iconIndex}`,
          type: 'scatter',
          coordinateSystem: 'geo',
          z: isSelected ? 1 : 0,
          data: [{
            value: coord,
            studioName: studioInfo.name,
          }],
          symbolSize: iconSize,
          symbol: `image://${badgeUrl || STUDIO_ICON_MAP[studioInfo.name as keyof typeof STUDIO_ICON_MAP]}`,
          itemStyle: { opacity: 1 },
          tooltip: { show: false },
          silent: true,
          animation: false,
        });
        iconIndex += 1;
      });
    });

    // 渲染选中的国家（可能多个）和所有 icon
    hoveredChartInstance.current.setOption({
      series: [
        {
          id: 'hovered-map',
          data: countryNamesToHighlight.map((name) => ({ name, value: 1 })),
        },
        ...iconSeries,
      ],
    }, { replaceMerge: ['series'] });
  }, [hoveredStudioName, hoveredChartRef, countryMarkerData, studioRelationMap]);

  return (
    <div className="dbd-studio-map" ref={containerRef}>
      {mapLoading && (
        <div className="dbd-studio-map-loading">
          <Loading />
        </div>
      )}
      <div ref={chartRef} className="dbd-studio-map-container" />
      <div className="dbd-studio-map-zoom-btn">
        <Icon type="plus" onClick={() => setMapZoom({ type: 'zoomIn', toChange: Date.now() })} />
        <Icon type="minus" onClick={() => setMapZoom({ type: 'zoomOut', toChange: Date.now() })} />
      </div>
    </div>
  );
};

export default StudioMap;
